(() => {
  'use strict';

  const SH = window.SassyHive = window.SassyHive || {};
  SH.keys = {
    draft: 'sassyHiveQuizDraftV1',
    edit: 'sassyHiveEditCustomizationV2'
  };
  SH.consentVersion = '2026-08-03-v1';
  SH.root = window.Shopify?.routes?.root || '/';

  const configNode = document.querySelector('[data-sassy-hive-cart-config]');
  try {
    SH.config = configNode ? JSON.parse(configNode.textContent) : {};
  } catch (error) {
    console.error('The Sassy Hive configuration could not be read.', error);
    SH.config = {};
  }
  SH.config = {
    tiers: {},
    video: {},
    currency: window.Shopify?.currency?.active || 'USD',
    cartUrl: `${SH.root}cart`,
    tierPageUrl: `${SH.root}#shop`,
    supportEmail: '',
    ...SH.config
  };

  const videoCents = Number(SH.config.video?.priceCents);
  SH.featureVideoPrice = Number.isFinite(videoCents) && videoCents > 0 ? videoCents / 100 : 20;

  SH.tiers = [
    { balls: 15, honey: 1, price: 39.95, emoji: '🎀', label: 'Sweet Start' },
    { balls: 20, honey: 2, price: 48.95, emoji: '🌸', label: 'Sassy Favorite' },
    { balls: 25, honey: 3, price: 57.95, emoji: '🐝', label: 'Perfect Balance', featured: true },
    { balls: 30, honey: 4, price: 66.95, emoji: '✨', label: 'Extra Sassy' },
    { balls: 35, honey: 5, price: 75.95, emoji: '💝', label: 'Biggest Box' }
  ];

  SH.state = {
    selectedTier: SH.tiers[0],
    currentStep: 1,
    activeEdit: null
  };

  SH.normalizeVariantId = (value) => {
    const normalized = String(value ?? '').trim();
    return /^\d+$/.test(normalized) ? normalized : '';
  };

  SH.resolveTier = (tierNumber) => {
    const key = String(tierNumber);
    const direct = SH.config.tiers?.[key] || {};
    const directId = SH.normalizeVariantId(direct.variantId);
    if (directId) {
      return {
        variantId: directId,
        priceCents: Number(direct.priceCents),
        available: direct.available !== false,
        title: direct.variantTitle || `${key} Mystery Charms`
      };
    }

    const wanted = String(tierNumber);
    const matched = (SH.config.productVariants || []).find((variant) => {
      const sources = [variant?.title, variant?.sku, ...(variant?.options || [])];
      return sources.some((source) => (String(source ?? '').match(/\d+/g) || []).includes(wanted));
    });
    if (!matched) return null;
    return {
      variantId: SH.normalizeVariantId(matched.id),
      priceCents: Number(matched.priceCents),
      available: matched.available !== false,
      title: matched.title || `${key} Mystery Charms`
    };
  };

  SH.tiers.forEach((tier) => {
    const resolved = SH.resolveTier(tier.balls);
    if (!resolved) return;
    const cents = Number(resolved.priceCents);
    if (Number.isFinite(cents) && cents > 0) tier.price = cents / 100;
    tier.variantId = resolved.variantId;
    tier.available = resolved.available;
    tier.variantTitle = resolved.title;
  });

  SH.formatMoney = (amount) => {
    const currency = SH.config.currency || window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency
      }).format(amount);
    } catch (_) {
      return `${Number(amount).toFixed(2)} ${currency}`;
    }
  };

  SH.escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;'
  })[character]);

  SH.prefersReducedMotion = () =>
    window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true;

  let toastTimer = 0;
  SH.toast = (message) => {
    let node = document.getElementById('toast');
    if (!node) {
      node = document.createElement('div');
      node.id = 'toast';
      node.className = 'toast';
      node.setAttribute('role', 'status');
      node.setAttribute('aria-live', 'polite');
      document.body.appendChild(node);
    }
    node.textContent = message;
    node.classList.add('show');
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => node.classList.remove('show'), 4300);
  };

  SH.createCustomizationId = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `sassy-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  };

  const rewards = [
    ['🐝', 'Bee Charms', 'Extra Honey Drop', 'Adds another premium-item selection.', 'Bee Charm → Honey Drop → Premium Item'],
    ['🧜‍♀️', 'Mermaid Charms', 'Mermaid Pick', 'Adds an ocean- or coastal-themed surprise.', 'Mermaid Charm → Mermaid Pick'],
    ['🏺', 'Jar Charms', 'Earring Pick', 'Adds a silly, sassy, fun pair of earrings.', 'Jar Charm → Earring Pick'],
    ['🐾', 'Animal Charms', 'Extra Mystery Charm', 'Adds another regular-item selection.', 'Animal Charm → Mystery Charm → Regular Item']
  ];

  const tierCard = (tier) => {
    const resolved = SH.resolveTier(tier.balls);
    const available = Boolean(SH.normalizeVariantId(resolved?.variantId)) && resolved?.available !== false;
    return `<article class="tier-card ${tier.featured ? 'featured' : ''} ${available ? '' : 'is-unavailable'}">
      ${tier.featured ? '<span class="ribbon">PERFECT BALANCE</span>' : ''}
      <div class="tier-visual"><span aria-hidden="true">${tier.emoji}</span></div>
      <div class="tier-body">
        <div class="eyebrow">${SH.escapeHtml(tier.label)}</div>
        <h3>${tier.balls} Mystery Charms</h3>
        <div class="price">${SH.formatMoney(tier.price)}</div>
        <ul>
          <li>${tier.balls} regular-item selections</li>
          <li>${tier.honey} guaranteed Honey Drop${tier.honey > 1 ? 's' : ''}</li>
          <li>May include complimentary Buzzy Bonus Charms</li>
        </ul>
        <button class="btn btn-primary btn-sm" type="button" data-tier-choice="${tier.balls}" ${available ? '' : 'disabled aria-disabled="true"'}>
          ${available ? `Choose ${tier.balls}-Item Box` : 'Temporarily Unavailable'}
        </button>
        <span class="micro">${available
          ? `Feature Video available for +${SH.formatMoney(SH.featureVideoPrice)}`
          : 'This tier must be connected or restocked in Shopify.'}</span>
      </div>
    </article>`;
  };

  const renderContent = () => {
    document.querySelectorAll('[data-standalone-price]').forEach((element) => {
      const price = SH.config.standalone?.[element.dataset.standalonePrice]?.priceCents;
      if (price === null || price === undefined) return;
      const cents = Number(price);
      if (!Number.isFinite(cents) || cents < 0) return;
      element.textContent = SH.formatMoney(cents / 100);
    });
    document.querySelectorAll('#homeTierGrid,#shopTierGrid,#honeyTierGrid').forEach((element) => {
      element.innerHTML = SH.tiers.map(tierCard).join('');
    });

    document.querySelectorAll('#homeRewardGrid,#buzzRewardGrid').forEach((element) => {
      element.innerHTML = rewards.map(([emoji, name, reward, description, flow]) =>
        `<article class="reward-card"><div class="emoji" aria-hidden="true">${emoji}</div>
          <h3>${SH.escapeHtml(name)}</h3><strong>${SH.escapeHtml(reward)}</strong>
          <p>${SH.escapeHtml(description)}</p><div class="flow">${SH.escapeHtml(flow)}</div></article>`
      ).join('');
    });

    const faqs = [
      ['What is a Sweet & Sassy Mystery Box?', 'It is a personalized surprise merchandise box revealed through The Sassy Hive’s Mystery Charm system. You choose a tier, complete your Hive Profile, and Rhonda uses your answers to guide the eligible inventory while the exact selections remain a surprise.'],
      ['Will I receive the reusable Mystery Charms?', 'No. Mystery Charms are reusable pieces used by The Sassy Hive during the selection and reveal process. You receive the merchandise revealed through the charms.'],
      ['Does every regular Mystery Charm produce an item?', 'Yes. Every regular Mystery Charm selection adds one regular merchandise item to your order. Complimentary additions may occasionally be included.'],
      ['What is a Honey Drop?', 'A Honey Drop is a higher-value premium-item selection included in addition to the regular Mystery Charms.'],
      ['What are Buzzy Bonus Charms?', 'Buzzy Bonus Charms are complimentary surprise additions that may be discovered while an order is being prepared. They are not guaranteed unless an offer states otherwise.'],
      ['Does a Buzzy Bonus Charm replace a regular item?', 'No. When discovered, a Buzzy Bonus Charm adds another surprise to the current order.'],
      ['How does customization work?', 'Your quiz helps us curate around your colors, hopes, dislikes, metal preference, interests, and notes while keeping the exact items a surprise.'],
      ['What is the Feature Video?', `For an additional ${SH.formatMoney(SH.featureVideoPrice)}, we record the Mystery Charm selection and reveal. With your consent, it may appear on The Sassy Hive’s website or social-media channels.`],
      ['Do I need to watch live?', 'No. The video is recorded after the order is placed.'],
      ['Can I purchase without a video?', 'Yes. We will still curate, pack, and ship your Mystery Box.']
    ];
    const faq = document.getElementById('faqList');
    if (faq) faq.innerHTML = faqs.map(([q, a]) =>
      `<details><summary>${SH.escapeHtml(q)}</summary><p>${SH.escapeHtml(a)}</p></details>`
    ).join('');
  };

  const titles = {
    home: 'The Sassy Hive', shop: 'Mystery Box Tiers', 'how-it-works': 'How It Works',
    'honey-drops': 'Honey Drops', 'buzz-bonuses': 'Buzzy Bonus Charms',
    'feature-video': 'Feature Video', about: 'Our Story',
    faq: 'Frequently Asked Questions', contact: 'Contact',
    customize: 'Customize Your Mystery Box'
  };

  const closeMenu = (restoreFocus = false) => {
    const nav = document.getElementById('mobileNav');
    const button = document.querySelector('[data-mobile-menu-toggle]');
    if (!nav || !button) return;
    nav.classList.remove('open');
    nav.setAttribute('aria-hidden', 'true');
    button.setAttribute('aria-expanded', 'false');
    if (restoreFocus) button.focus();
  };

  const route = (focus = false) => {
    const app = document.getElementById('sassyHiveApp');
    if (!app) return;
    const raw = (window.location.hash || '#home').slice(1).split('?')[0];
    const name = Object.hasOwn(titles, raw) ? raw : 'home';
    const story = document.querySelector('[data-sh-storybook-about]');
    const about = name === 'about';

    document.querySelectorAll('#sassyHiveApp .page').forEach((page) => {
      const active = !about && page.dataset.page === name;
      page.classList.toggle('active', active);
      page.setAttribute('aria-hidden', String(!active));
    });
    if (story) {
      story.hidden = !about;
      story.setAttribute('aria-hidden', String(!about));
      if (about) window.dispatchEvent(new CustomEvent('sassy:storybook-open', { detail: story }));
    }

    document.querySelectorAll('.nav-links a,.mobile-nav a').forEach((link) => {
      const active = (link.getAttribute('href') || '').endsWith(`#${name}`);
      link.classList.toggle('active', active);
      if (active) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    });
    closeMenu();
    document.title = name === 'home' ? titles[name] : `${titles[name]} – The Sassy Hive`;

    const target = about ? story : document.querySelector('#sassyHiveApp .page.active');
    window.scrollTo({ top: 0, behavior: focus && !SH.prefersReducedMotion() ? 'smooth' : 'auto' });
    if (focus) {
      window.setTimeout(() => {
        const heading = target?.querySelector('h1,h2');
        heading?.setAttribute('tabindex', '-1');
        heading?.focus({ preventScroll: true });
      }, SH.prefersReducedMotion() ? 0 : 220);
    }
  };
  SH.route = route;

  const clearNewBoxState = () => {
    const url = new URL(window.location.href);
    if (url.searchParams.get('new_box') !== '1') return;
    try {
      sessionStorage.removeItem(SH.keys.draft);
      sessionStorage.removeItem(SH.keys.edit);
    } catch (_) {}
    SH.state.activeEdit = null;
    url.searchParams.delete('new_box');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash || '#shop'}`);
  };

  const initializeNavigation = () => {
    const button = document.querySelector('[data-mobile-menu-toggle]');
    const nav = document.getElementById('mobileNav');
    if (button && nav) {
      nav.setAttribute('aria-hidden', 'true');
      button.addEventListener('click', () => {
        if (button.getAttribute('aria-expanded') === 'true') {
          closeMenu(true);
        } else {
          nav.classList.add('open');
          nav.setAttribute('aria-hidden', 'false');
          button.setAttribute('aria-expanded', 'true');
          nav.querySelector('a')?.focus();
        }
      });
      nav.addEventListener('click', (event) => {
        if (event.target.closest('a')) closeMenu();
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && button.getAttribute('aria-expanded') === 'true') closeMenu(true);
      });
      document.addEventListener('click', (event) => {
        if (button.getAttribute('aria-expanded') === 'true' && !nav.contains(event.target) && !button.contains(event.target)) closeMenu();
      });
    }

    document.querySelectorAll('[data-return-to-tiers]').forEach((link) => {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        window.location.assign(window.SassyHiveTierUrl || SH.config.tierPageUrl || `${SH.root}#shop`);
      });
    });

    const subject = document.getElementById('ContactFormSubject');
    if (subject) {
      const update = () => {
        const guidance = document.getElementById('ContactSafetyGuidance');
        if (guidance) guidance.hidden = subject.value !== 'Product reaction or safety concern';
      };
      subject.addEventListener('change', update);
      update();
    }
    document.querySelector('.form-success')?.focus({ preventScroll: true });
  };

  const initialize = () => {
    clearNewBoxState();
    renderContent();
    initializeNavigation();
    if (document.getElementById('sassyHiveApp')) {
      window.addEventListener('hashchange', () => route(true));
      route(false);
    }
    window.dispatchEvent(new CustomEvent('sassy:core-ready'));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
